import type { AttendancePunchSource } from '../types/attendance';

export type AttendanceSourceKind = 'KIOSK' | 'MOBILE' | 'MANUAL' | 'OTHER';

export type AttendanceSourceDisplay = AttendancePunchSource & {
  kind: AttendanceSourceKind;
  label: string | null;
  originalLabel: string | null;
  showManualEdit: boolean;
};

const KIOSK_CODES = new Set([
  'KIOSK',
  'OFFICE_KIOSK',
  'FACE_KIOSK',
  'FACE',
  'OFFICE',
]);
const MOBILE_CODES = new Set(['MOBILE', 'MOBILE_APP']);
const WEB_PORTAL_CODES = new Set(['WEB_PORTAL', 'WEBPORTAL', 'WEB']);
const MANUAL_CODES = new Set(['MANUAL', 'MANUAL_EDIT']);

function text(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function normalizeSourceCode(value: string | null | undefined): string {
  return typeof value === 'string'
    ? value.trim().toUpperCase().replace(/[\s/-]+/g, '_')
    : '';
}

function numberId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim() && /^\d+$/.test(value.trim())) {
    const parsed = Number(value.trim());
    return parsed > 0 ? parsed : null;
  }
  return null;
}

function firstNumber(
  row: Record<string, unknown>,
  keys: string[],
): number | null {
  for (const key of keys) {
    const value = numberId(row[key]);
    if (value) return value;
  }
  return null;
}

/**
 * Kiosk is identified by a real kiosk id (or an original kiosk source).
 * Face verification alone is not a kiosk signal — mobile punches also verify faces.
 */
function hasKioskSignal(punch?: AttendancePunchSource | null): boolean {
  if (!punch) return false;
  if (numberId(punch.kioskId) != null) return true;
  return classifyAttendanceSource(punch.originalSource) === 'KIOSK';
}

/** Maps a stored attendance_source code to a display kind. Unknown codes stay OTHER. */
export function classifyAttendanceSource(
  source: string | null | undefined,
  punch?: AttendancePunchSource | null,
): AttendanceSourceKind {
  const code = normalizeSourceCode(source);
  if (!code) return 'OTHER';
  if (MANUAL_CODES.has(code)) return 'MANUAL';
  if (KIOSK_CODES.has(code)) return 'KIOSK';
  if (MOBILE_CODES.has(code)) return 'MOBILE';
  if (WEB_PORTAL_CODES.has(code)) {
    return hasKioskSignal(punch) ? 'KIOSK' : 'MOBILE';
  }
  return 'OTHER';
}

/** Friendly label for a stored source. Unknown values are shown as returned by the API. */
export function attendanceSourceLabel(
  source: string | null | undefined,
  punch?: AttendancePunchSource | null,
): string | null {
  const kind = classifyAttendanceSource(source, punch);
  if (kind === 'KIOSK') return 'Kiosk';
  if (kind === 'MOBILE') return 'Mobile';
  if (kind === 'MANUAL') return 'Manual Edit';
  return text(source);
}

export type PunchActionSourceInput = {
  source?: string | null;
  originalSource?: string | null;
  kioskName?: string | null;
  kioskId?: number | null;
  faceVerified?: boolean | null;
  deviceIdentifier?: string | null;
};

function punchFromAction(punch: PunchActionSourceInput): AttendancePunchSource {
  return {
    source: punch.source ?? null,
    originalSource: punch.originalSource ?? null,
    kioskId: punch.kioskId ?? null,
    faceVerified: punch.faceVerified ?? null,
    deviceIdentifier: punch.deviceIdentifier ?? null,
  };
}

function hasPunchActionSignal(punch: PunchActionSourceInput): boolean {
  return Boolean(
    punch.source ||
      punch.originalSource ||
      punch.kioskName?.trim() ||
      punch.kioskId ||
      punch.faceVerified === true ||
      punch.deviceIdentifier,
  );
}

export function punchActionSourceKind(
  punch: PunchActionSourceInput,
): AttendanceSourceKind | null {
  if (!hasPunchActionSignal(punch)) return null;
  return classifyAttendanceSource(punch.source, punchFromAction(punch));
}

/** Kiosk punches use the stored kiosk name; other punches use the classified source label. */
export function punchActionSourceLabel(
  punch: PunchActionSourceInput,
): string | null {
  const meta = punchFromAction(punch);
  const kind = punchActionSourceKind(punch);
  if (!kind) return null;
  if (kind === 'KIOSK') {
    const kioskName = punch.kioskName?.trim();
    if (kioskName) return kioskName;
    return attendanceSourceLabel(punch.source, meta);
  }
  return (
    attendancePunchChannelLabel(punch.source, meta) ??
    attendanceSourceLabel(punch.source, meta)
  );
}

export function isMixedPunchActionSource(
  checkIn: PunchActionSourceInput,
  checkOut: PunchActionSourceInput,
): boolean {
  const checkInKind = punchActionSourceKind(checkIn);
  const checkOutKind = punchActionSourceKind(checkOut);
  return (
    checkInKind != null && checkOutKind != null && checkInKind !== checkOutKind
  );
}

/**
 * Daily Attendance Type channel: kiosk/office punches are Office, app punches
 * are Mobile. Unknown codes stay as returned by the API.
 */
export function attendancePunchChannelLabel(
  source: string | null | undefined,
  punch?: AttendancePunchSource | null,
): string | null {
  const kind = classifyAttendanceSource(source, punch);
  if (kind === 'KIOSK') return 'Office';
  if (kind === 'MOBILE') return 'Mobile';
  if (kind === 'MANUAL') return 'Manual';
  const code = normalizeSourceCode(source);
  if (
    code === 'WFH' ||
    code === 'WFH_APP' ||
    code === 'CLIENT_OFFICE' ||
    code === 'CLIENT_PROSPECT_VISIT' ||
    code === 'CLIENT_VISIT' ||
    code === 'PROSPECT_VISIT'
  ) {
    return 'Mobile';
  }
  return attendanceSourceLabel(source, punch);
}

export function emptyAttendancePunchSource(): AttendancePunchSource {
  return {
    source: null,
    originalSource: null,
    isManuallyEdited: null,
    editedByName: null,
    editedAt: null,
    editReason: null,
  };
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return null;
}

function firstText(
  row: Record<string, unknown>,
  keys: string[],
): string | null {
  for (const key of keys) {
    const value = text(row[key]);
    if (value) return value;
  }
  return null;
}

/** Reads source metadata from a camelCase or snake_case API row. */
export function punchSourceFromApiRow(
  row: Record<string, unknown> | null | undefined,
  prefix = '',
): AttendancePunchSource {
  const sourceRow = row ?? {};
  const withPrefix = (name: string) =>
    prefix ? `${prefix}${name.charAt(0).toUpperCase()}${name.slice(1)}` : name;
  const withSnake = (name: string) =>
    prefix
      ? `${prefix.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)}_${name}`
      : name;

  const sourceKeys = prefix
    ? [
        withPrefix('source'),
        withPrefix('attendanceSource'),
        withSnake('source'),
        withSnake('attendance_source'),
      ]
    : [
        'attendanceSource',
        'attendance_source',
        'source',
        'attendanceMode',
      ];
  const originalKeys = prefix
    ? [
        withPrefix('originalSource'),
        withPrefix('originalAttendanceSource'),
        withSnake('original_source'),
        withSnake('original_attendance_source'),
      ]
    : [
        'originalAttendanceSource',
        'original_attendance_source',
        'originalSource',
      ];
  const source = firstText(sourceRow, sourceKeys);
  const originalSource = firstText(sourceRow, originalKeys);
  const editedByName = firstText(sourceRow, [
    withPrefix('editedByName'),
    withPrefix('editedBy'),
    withSnake('edited_by_name'),
    'editedByName',
    'edited_by_name',
  ]);
  const editedAt = firstText(sourceRow, [
    withPrefix('editedAt'),
    withSnake('edited_at'),
    'editedAt',
    'edited_at',
  ]);
  const editReason = firstText(sourceRow, [
    withPrefix('editReason'),
    withPrefix('manualEditReason'),
    withSnake('edit_reason'),
    'editReason',
    'edit_reason',
  ]);
  const editedFlag = boolOrNull(
    sourceRow[withPrefix('isManuallyEdited')] ??
      sourceRow[withPrefix('manuallyEdited')] ??
      sourceRow[withSnake('is_manually_edited')] ??
      sourceRow.isManuallyEdited ??
      sourceRow.is_manually_edited,
  );
  const faceVerified = boolOrNull(
    sourceRow[withPrefix('faceVerified')] ??
      sourceRow[withSnake('face_verified')] ??
      sourceRow.faceVerified ??
      sourceRow.face_verified,
  );
  const kioskId = firstNumber(sourceRow, [
    withPrefix('kioskId'),
    withSnake('kiosk_id'),
    'kioskId',
    'kiosk_id',
  ]);
  const faceVerificationLogId = firstNumber(sourceRow, [
    withPrefix('faceVerificationLogId'),
    withSnake('face_verification_log_id'),
    'faceVerificationLogId',
    'face_verification_log_id',
  ]);
  const deviceIdentifier = firstText(sourceRow, [
    withPrefix('deviceIdentifier'),
    withPrefix('deviceName'),
    withSnake('device_identifier'),
    'deviceIdentifier',
    'device_identifier',
    'deviceName',
  ]);

  return {
    source,
    originalSource,
    isManuallyEdited: editedFlag,
    editedByName:
      editedByName && !/^\d+$/.test(editedByName) ? editedByName : null,
    editedAt,
    editReason,
    faceVerified,
    deviceIdentifier,
    kioskId,
    faceVerificationLogId,
  };
}

export function resolveAttendanceSourceDisplay(
  punch: AttendancePunchSource,
  options: { treatAsManualEdit?: boolean } = {},
): AttendanceSourceDisplay {
  const source = text(punch.source);
  const originalSource = text(punch.originalSource);
  const showManualEdit =
    options.treatAsManualEdit === true ||
    punch.isManuallyEdited === true ||
    classifyAttendanceSource(source, punch) === 'MANUAL';
  const kind = showManualEdit
    ? 'MANUAL'
    : classifyAttendanceSource(source, punch);
  const originalForLabel =
    originalSource && classifyAttendanceSource(originalSource, punch) !== 'MANUAL'
      ? originalSource
      : !showManualEdit
      ? null
      : originalSource && originalSource !== source
      ? originalSource
      : classifyAttendanceSource(source, punch) !== 'MANUAL'
      ? source
      : originalSource;
  const originalLabel = showManualEdit
    ? attendanceSourceLabel(originalForLabel, punch)
    : null;

  return {
    ...punch,
    source,
    originalSource,
    kind,
    label: showManualEdit
      ? 'Manual Edit'
      : attendanceSourceLabel(source, punch),
    originalLabel:
      originalLabel && originalLabel !== 'Manual Edit' ? originalLabel : null,
    showManualEdit,
  };
}

export function attendanceSourceCaption(display: AttendanceSourceDisplay): string | null {
  if (!display.label) return null;
  if (display.showManualEdit && display.originalLabel) {
    return `${display.label} · Originally ${display.originalLabel}`;
  }
  return display.label;
}

export function attendanceSourceAccessibility(
  display: AttendanceSourceDisplay,
): string | null {
  return attendanceSourceCaption(display);
}
